import { CommandBar, Dialog, Link, ICommandBarItemProps, IconButton, Label, ProgressIndicator, Stack, StackItem, VerticalDivider } from '@fluentui/react';
import * as React from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { b64toBlob, fileExtensionsForImages, getFileName, getImageDimensions, getMegabytesToBytes, handledFileTypes, officeFileExtensions, onOpenFileInTab, saveBase64, sendApiRequest, thisIsDevEnvironment, UNKNOWN_IMAGE_BASE_64 } from '../lib/helper';
import { useAccreditationRulesContext } from '../providers/accreditationrulesprovider';
import { useAuthenticationContext } from '../providers/authenticationprovider';
import { HelperService } from '../services/helperService';
import { ICourseWorkFlat } from '../types/IAssignment';
import { EmbedPDF } from './shared/PDFEmbed/EmbedPDF';

interface IImageSize {
    width: number;
    height: number;
}

export interface ISlideshowProps {
    files?: ICourseWorkFlat[],
    currentFile?: ICourseWorkFlat,
    onClose: () => void;
    maxFileSizeBeforeStream: number;
}

export function SlideShow(props: ISlideshowProps) {
    const location = useLocation();
    const authContext = useAuthenticationContext();
    const accreditorRulesContext = useAccreditationRulesContext();

    const state = location.state as ISlideshowProps;
    const files = props.files || state?.files || [];

    const [currentImage, setCurrentImage] = useState<ICourseWorkFlat | undefined>(props.currentFile || state?.currentFile);
    const [base64, setBase64] = React.useState<string>();
    const [thumbnailBase64, setThumbnailBase64] = React.useState<string>();
    const [imageSize, setImageSize] = React.useState<IImageSize>();
    const [isLoadingImage, setIsLoadingImage] = React.useState(true);
    const [windowSize, setWindowSize] = React.useState<IImageSize>();
    const [panelShowing, setPanelShowing] = React.useState(false);
    const [fileTooLarge, setFileTooLarge] = React.useState<number | undefined>();
    const currentFile = props.currentFile || state?.currentFile;

    const isImage = fileExtensionsForImages.includes(currentImage?.FileExtension?.toLowerCase() || "");
    const [fileHelperServer, setFileHelperServer] = React.useState<string>();

    React.useEffect(() => {
        HelperService.getKeplerEnvironmentVariable("REACT_APP_KEPLER_FILE_HELPER_SERVER").then((res: string | undefined) => {
            setFileHelperServer(res);
        });
    }, []);

    React.useEffect(() => {
        setBase64(undefined);
        setThumbnailBase64(undefined);
        setIsLoadingImage(true);
        setImageSize(undefined);
        setFileTooLarge(undefined);
        if (fileHelperServer) {
            if (currentImage?.FileExtension && handledFileTypes.includes(currentImage.FileExtension.toLowerCase()) && !isLoadingImage) {
                if ([".mov", ".mp4", ".pdf"].includes(currentImage?.FileExtension?.toLowerCase() || "")) {
                    const maxFileSizeBytes = getMegabytesToBytes(props.maxFileSizeBeforeStream);
                    if (currentImage.FileExtension === ".pdf" && ((currentImage.BlobProperties.contentLength || 0) > maxFileSizeBytes)) {
                        setFileTooLarge(props.maxFileSizeBeforeStream);
                        setIsLoadingImage(false);
                    }
                    else {
                        const thumbnailApiUrl = `/api/blobStorage/getBlobAsBase64FromPathForPreview?path=${encodeURIComponent(currentImage.FilePath)}`;
                        sendApiRequest(thumbnailApiUrl, "GET").then(res => {
                            setThumbnailBase64(res.base64);
                            if (currentImage.FileExtension.toLowerCase() === ".pdf" && res.base64) {
                                getImageDimensions(res.base64).then((size: { w: number, h: number }) => {
                                    setImageSize({ width: size.w, height: size.h });
                                });
                            }
                        });
                    }
                }
                if (currentFile && officeFileExtensions.includes(currentFile.FileExtension.toLowerCase())) {
                    if (fileHelperServer && fileHelperServer.length > 0) {
                        setBase64(currentImage.FilePath);
                    }
                }
                else {
                    const url = `/api/blobStorage/getBlobAsBase64FromPath?path=${encodeURIComponent(currentImage.FilePath)}`;
                    sendApiRequest(url, "GET").then(res => {
                        const imageData = res;
                        setBase64(res);
                        setIsLoadingImage(false);
                        if (imageData) {
                            getImageDimensions(imageData, currentImage.FileExtension === ".svg" ? "image/svg+xml" : undefined).then((size: { w: number, h: number }) => {
                                setImageSize({ width: size.w, height: size.h });
                            })
                        }
                    });
                }
            }
            else {
                setIsLoadingImage(false);
            }
        }
        return () => {
            setBase64(undefined);
            setImageSize(undefined);
        }
    }, [currentImage, fileHelperServer, props.maxFileSizeBeforeStream]);

    React.useEffect(() => {
        setIsLoadingImage(true);
        setIsLoadingImage(false);
    }, [windowSize])

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        })
    }

    React.useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        }
    }, [])


    const currentFileIndex = files.findIndex(f => currentImage && f.FilePath === currentImage.FilePath);
    const currentFileName = currentImage && getFileName(authContext.Result, accreditorRulesContext?.Result, currentImage, currentFileIndex);

    const _getNavigationCommandBarItems = (): ICommandBarItemProps[] => {
        let retVal: ICommandBarItemProps[] = [{
            key: "navigator",
            commandBarButtonAs: () => {
                return <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 14 }}>
                    <IconButton iconProps={{ iconName: "Info" }} onClick={() => {
                        setPanelShowing(!panelShowing)
                    }} />
                    <VerticalDivider styles={{ wrapper: { height: 20 } }} />
                    <IconButton disabled={currentFileIndex === 0} iconProps={{ iconName: "PlayReverseResume" }} onClick={() => {
                        let num = currentFileIndex - 1;
                        setCurrentImage(files[num]);
                    }} />
                    <div>{currentFileIndex + 1} / {files.length}</div>
                    <IconButton disabled={currentFileIndex === files.length - 1} iconProps={{ iconName: "PlayResume" }} onClick={() => {
                        let num = currentFileIndex + 1;
                        setCurrentImage(files[num]);
                    }} />
                    <VerticalDivider styles={{ wrapper: { height: 20 } }} />
                    <IconButton iconProps={{ iconName: "ChromeClose" }} onClick={() => {
                        //navigate(location.pathname.substring(0, location.pathname.lastIndexOf("/")));
                        props.onClose();
                    }} />
                </Stack>
            }
        }];

        return retVal;
    }

    const _getDocumentCommandBarItems = (): ICommandBarItemProps[] => {
        const apiPath = currentImage?.FilePath && `/api/blobStorage/getFileStream/${currentImage.FileName}?path=${encodeURIComponent(currentImage.FilePath)}&attachment=true`;
        const url = thisIsDevEnvironment ? `https://localhost:8080${apiPath}` : apiPath;
        let retVal: ICommandBarItemProps[] = [{
            key: "download",
            iconProps: {
                iconName: "Download"
            },
            text: "Download",
            href: url,
            target: "_blank",
            download: true
        }];

        return retVal;
    }

    const maxImageHeight = window.innerHeight - 70;
    const maxImageWidth = window.innerWidth - 70;
    const modalHeight = "calc(100vh)";

    let imageWidth = 100;
    let imageHeight = 100;
    if (imageSize) {
        imageHeight = imageSize.height;
        imageWidth = imageSize.width;
        if (imageSize.height > window.innerHeight) {
            imageHeight = maxImageHeight;
        }
        if (imageSize.width > window.innerWidth) {
            imageWidth = maxImageWidth;
            if (panelShowing) {
                imageWidth = imageWidth - 300;
            }
        }
    }
    if (thumbnailBase64) {
        imageHeight = maxImageHeight;
        imageWidth = maxImageWidth;
    }

    const imageProperties = {
        Name: currentFileName,
        Student: currentImage?.Student,
        Tags: currentImage?.Tags?.filter(t => t && t.length > 0).join(", ")
    }
    
    const _renderContent = () => {
        const apiPath = currentImage?.FilePath && `/api/blobStorage/getFileStream/${currentImage.FileName}?path=${encodeURIComponent(currentImage.FilePath)}`;
        const url = thisIsDevEnvironment ? `https://localhost:8080${apiPath}` : apiPath;
        let el = fileTooLarge ? <Stack tokens={{childrenGap: 10}} horizontalAlign="center" verticalAlign="center">
            <div style={{marginTop: 100}}>
                The file is larger than {fileTooLarge} MB and therefore no preview is available
            </div>
            {currentImage?.FilePath && apiPath &&  <Link title={currentImage.FileName} href={url} onClick={(ev) => onOpenFileInTab(ev, url!, currentImage.FileName)}>Click here to download the file</Link>}
            </Stack> : <div>Unable to find content renderer for {currentImage?.FileExtension}</div>;
        let imgType = "image/png";
        if (currentImage?.FileExtension === ".svg") {
            imgType = "image/svg+xml";
        }
        if (!fileTooLarge) {
            if (isImage && currentImage) {
                el = <Stack>
                    <img className="image-slider-section" src={`data:${imgType};base64,${base64 || thumbnailBase64 || UNKNOWN_IMAGE_BASE_64}`} style={{ margin: 10, objectFit: "contain", height: imageHeight, maxWidth: imageWidth }} alt={`${currentFileName} by ${currentImage.Student}`} />
                    {isLoadingImage && <ProgressIndicator />}
                </Stack>
            }
            else if (base64) {
                switch (currentImage?.FileExtension.toLowerCase()) {
                    case ".pdf":
                        el = <div style={{
                            margin: 10,
                            height: imageHeight,
                            width: "auto"
                        }}>
                            <EmbedPDF fileBlob={b64toBlob(base64)} fileName={currentFileName || "Unknown"} showToolbar={true} width={`calc(100vw - ${panelShowing ? 300 : 44}px)`} />
                        </div>
                        break;
                    case ".mov":
                    case ".mp4":
                        const videoApiPath = `/api/blobStorage/getVideo?path=${encodeURIComponent(currentImage.FilePath)}`;
                        const apiPath = thisIsDevEnvironment ? `https://localhost:8080${videoApiPath}` : videoApiPath;
                        el = <video style={{ height: "calc(100vh - 44px)" }} controls>
                            <source src={apiPath} type="video/mp4" />
                        </video>
                        break;
                    case ".docx":
                    case ".docm":
                    case ".dotm":
                    case ".dotx":
                    case ".doc":
                    case ".pptx":
                    case ".ppsx":
                    case ".ppt":
                    case ".pps":
                    case ".pptm":
                    case ".potm":
                    case ".ppam":
                    case ".potx":
                    case ".ppsm":
                    case ".xlsx":
                    case ".xlsb":
                    case ".xls":
                    case ".xlsm":
                        const keplerOfficeServerUrl = fileHelperServer;
                        const fileUrl = `${keplerOfficeServerUrl}${currentImage.FilePath}`;
                        const filePath = thisIsDevEnvironment ? "newteach.pbworks.com%2Ff%2Fele%2Bnewsletter.docx" : encodeURIComponent(fileUrl);
                        el = <iframe title={currentImage.FileName} style={{ height: "calc(100vh - 44px)", width: "100%" }} src={`https://view.officeapps.live.com/op/embed.aspx?src=${filePath}`} />
                        break;
                }
            }
            else if (currentFile?.FileExtension === ".pdf" && thumbnailBase64) {
                el = <Stack>
                    {isLoadingImage && <ProgressIndicator label="Loading full PDF" />}
                </Stack>
            }
            else if (currentImage) {
                el = <Stack>
                    {isLoadingImage && <ProgressIndicator />}
                    <img className="image-slider-section" src={`data:image/png;base64,${thumbnailBase64 || UNKNOWN_IMAGE_BASE_64}`} style={{ margin: 10, objectFit: "contain", height: imageHeight, maxWidth: imageWidth }} alt={`${currentFileName} by ${currentImage.Student}`} />
                </Stack>
            }
        }
        return el;
    }

    return <Dialog
        hidden={false}
        //onDismiss={onClose}     
        dialogContentProps={{ styles: { header: { display: "none" }, inner: { padding: 0 } } }}
        modalProps={{
            //isBlocking: true,            
            isDarkOverlay: true,
            styles: {
                main: {
                    height: modalHeight,
                    minHeight: modalHeight,
                    maxHeight: modalHeight,
                    width: "100vw !important",
                    minWidth: "100vw !important",
                    maxWidth: "100vw !important"
                }
            }
        }}
    >
        <Stack>
            <Stack horizontal grow horizontalAlign="space-between">
                <StackItem>
                    <CommandBar items={_getDocumentCommandBarItems()} />
                </StackItem>
                <StackItem align="center">
                    <div>{currentFileName}</div>
                </StackItem>
                <StackItem align="end">
                    <CommandBar items={_getNavigationCommandBarItems()} />
                </StackItem>
            </Stack>

            <Stack horizontal id="slideShowContainer" style={{ background: "#f5f5f5", height: "calc(100vh - 44px)" }}>
                <Stack.Item grow style={panelShowing ? { width: "calc(100vw - 300px)" } : undefined}>
                    <Stack horizontalAlign='center'>
                        {files.length > 0 ? _renderContent() : <div>Invalid state. <Link to={location.pathname.substring(0, location.pathname.lastIndexOf("/"))}>Click here</Link> to go back to assignment</div>}
                    </Stack>
                </Stack.Item>
                {panelShowing && <Stack.Item style={{ width: 300 }} disableShrink>
                    <div style={{ padding: 10, backgroundColor: "#efefef", height: "calc(100vh - 44px)" }}>
                        <h4>Properties</h4>
                        {Object.keys(imageProperties).map(key => {
                            return <Stack key={`slide-show-props-${key}`}>
                                <Label>{key}</Label>
                                <span>{(imageProperties as any)[key]}</span>
                            </Stack>
                        })}
                    </div></Stack.Item>}
            </Stack>
        </Stack>
    </Dialog>

};

export default SlideShow;